import React, { useEffect, useState, useCallback, useMemo } from 'react';
import MrcaLineageMap from './components/MrcaLineageMap';
import ChromosomePainterCombinedBars from './components/ChromosomePainterCombinedBars';
import ChromosomePainterAncestryRegionLayer from './components/ChromosomePainterAncestryRegionLayer';
import type { Segment, ChromPainterData, PainterSeg } from './components/ChromosomePainterCombinedBars';
import type { AncestryRegionByParentSeg } from './data/ancestryRegionsByParent';
import { loadHomePersonId, saveHomePersonId } from './utils/homePersonStorage';
import { loadMrcaPersistedState, MRCA_STATE_CHANGED_EVENT } from './utils/mrcaLineageModel';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:8000';

interface HomeSelectIndividual {
  id: string;
  first_name?: string;
  middle_names?: string;
  surname?: string;
}

function formatHomeIndividual(ind: HomeSelectIndividual): string {
  const parts = [ind.first_name, ind.middle_names, ind.surname].filter(Boolean).join(' ').trim();
  return parts || '(no name)';
}

function sortHomeIndividuals(a: HomeSelectIndividual, b: HomeSelectIndividual): number {
  const sa = (a.surname || '').toLowerCase();
  const sb = (b.surname || '').toLowerCase();
  if (sa !== sb) return sa.localeCompare(sb);
  return formatHomeIndividual(a).localeCompare(formatHomeIndividual(b));
}

interface PainterGroup {
  name: string;
  colour: string;
  num_segments: number;
  total_cm: number;
}

const chromLengths: { [chrom: string]: number } = {
  '1': 248956422, '2': 242193529, '3': 198295559, '4': 190214555,
  '5': 181538259, '6': 170805979, '7': 159345973, '8': 145138636,
  '9': 138394717, '10': 133797422, '11': 135086622, '12': 133275309,
  '13': 114364328, '14': 107043718, '15': 101991189, '16': 90338345,
  '17': 83257441, '18': 80373285, '19': 58617616, '20': 64444167,
  '21': 46709983, '22': 50818468,
};

const ChromosomePainterPage: React.FC = () => {
  const [homePersonId, setHomePersonId] = useState<string>(() => loadHomePersonId());
  const [homePersonOptions, setHomePersonOptions] = useState<HomeSelectIndividual[]>([]);

  // Progenitor view state
  const [progenitorData, setProgenitorData] = useState<ChromPainterData | null>(null);
  const [activeProgenitors, setActiveProgenitors] = useState<{ [id: string]: boolean }>({});
  const [hovered, setHovered] = useState<{ chrom: string; seg: Segment } | null>(null);
  const [fetchError, setFetchError] = useState<string | null>(null);

  // Group view state
  const [painterSegs, setPainterSegs] = useState<PainterSeg[]>([]);
  const [painterGroups, setPainterGroups] = useState<PainterGroup[]>([]);
  const [activeGroups, setActiveGroups] = useState<{ [name: string]: boolean }>({});
  const [sideFilter, setSideFilter] = useState<string>('All');
  const [groupHovered, setGroupHovered] = useState<PainterSeg | null>(null);

  // View toggle
  const [view, setView] = useState<'progenitor' | 'group' | 'mrca'>('progenitor');
  const [mrcaPersistRevision, setMrcaPersistRevision] = useState(0);
  /** MRCA tab only: colour DNA Painter bars by lineage path vs CSV group. */
  const [mrcaPathSegmentColorsEnabled, setMrcaPathSegmentColorsEnabled] = useState(true);
  /** MRCA tab chromosome chart: progenitor overlay vs DNA Painter match segments. */
  const [mrcaChromosomeShowProgenitor, setMrcaChromosomeShowProgenitor] = useState(true);
  const [mrcaChromosomeShowDnaPainter, setMrcaChromosomeShowDnaPainter] = useState(true);
  /** Underlay AncestryDNA-style regions by parent on chromosome bars (all views with bars). */
  const [showAncestryRegionsByParent, setShowAncestryRegionsByParent] = useState(false);
  const [groupViewAncestryHovered, setGroupViewAncestryHovered] = useState<AncestryRegionByParentSeg | null>(null);

  // Import modal state
  const [importOpen, setImportOpen] = useState(false);
  const [importCsv, setImportCsv] = useState('');
  const [clearExisting, setClearExisting] = useState(true);
  const [importing, setImporting] = useState(false);
  const [importResult, setImportResult] = useState<string | null>(null);

  useEffect(() => {
    saveHomePersonId(homePersonId);
  }, [homePersonId]);

  useEffect(() => {
    const onMrcaSaved = (e: Event) => {
      const d = (e as CustomEvent<{ homeId?: string }>).detail;
      if (d?.homeId === homePersonId) setMrcaPersistRevision((n) => n + 1);
    };
    window.addEventListener(MRCA_STATE_CHANGED_EVENT, onMrcaSaved);
    return () => window.removeEventListener(MRCA_STATE_CHANGED_EVENT, onMrcaSaved);
  }, [homePersonId]);

  const mrcaLinkedIndividualIds = useMemo(() => {
    void mrcaPersistRevision;
    return loadMrcaPersistedState(homePersonId).linkedIndividualIds;
  }, [homePersonId, mrcaPersistRevision]);

  useEffect(() => {
    let cancelled = false;
    fetch(`${API_URL}/individuals`)
      .then((res) => res.json())
      .then((data: HomeSelectIndividual[]) => {
        if (!cancelled) setHomePersonOptions([...data].sort(sortHomeIndividuals));
      })
      .catch(() => {
        if (!cancelled) setHomePersonOptions([]);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  // Load progenitor view data
  useEffect(() => {
    setFetchError(null);
    const hid = encodeURIComponent(homePersonId);
    fetch(`${API_URL}/home_person/${hid}/chromosome_painter`)
      .then(res => { if (!res.ok) throw new Error(); return res.json(); })
      .then((d: ChromPainterData) => {
        setProgenitorData(d);
        const allProgs: { [id: string]: boolean } = {};
        Object.values(d.chromosomes).forEach(chrom => {
          chrom.paternal.forEach((seg: Segment) => { allProgs[seg.progenitor_id] = true; });
          chrom.maternal.forEach((seg: Segment) => { allProgs[seg.progenitor_id] = true; });
        });
        setActiveProgenitors(allProgs);
      })
      .catch(() => setFetchError('Could not load progenitor chromosome data.'));
  }, [homePersonId]);

  // Load group view data
  const loadGroupData = useCallback(() => {
    const q = encodeURIComponent(homePersonId);
    Promise.all([
      fetch(`${API_URL}/dna_painter/segments?home_id=${q}`).then(r => r.json()),
      fetch(`${API_URL}/dna_painter/groups?home_id=${q}`).then(r => r.json()),
    ]).then(([segs, groups]) => {
      setPainterSegs(segs);
      setPainterGroups(groups);
      const ag: { [name: string]: boolean } = {};
      groups.forEach((g: PainterGroup) => { ag[g.name] = true; });
      setActiveGroups(ag);
    }).catch(() => {});
  }, [homePersonId]);

  useEffect(() => { loadGroupData(); }, [loadGroupData]);

  const handleImport = async () => {
    if (!importCsv.trim()) return;
    setImporting(true);
    setImportResult(null);
    try {
      const res = await fetch(`${API_URL}/dna_painter/import`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ home_id: homePersonId, csv_text: importCsv, clear_existing: clearExisting }),
      });
      const data = await res.json();
      setImportResult(`Imported ${data.imported} segments successfully.`);
      setImportCsv('');
      loadGroupData();
    } catch {
      setImportResult('Import failed. Please check your CSV format.');
    }
    setImporting(false);
  };

  // Progenitor legend
  const allProgenitors: { [id: string]: string } = {};
  if (progenitorData) {
    Object.keys(progenitorData.chromosomes).forEach(chrom => {
      progenitorData.chromosomes[chrom].paternal.forEach((seg: Segment) => { allProgenitors[seg.progenitor_id] = seg.progenitor_name; });
      progenitorData.chromosomes[chrom].maternal.forEach((seg: Segment) => { allProgenitors[seg.progenitor_id] = seg.progenitor_name; });
    });
  }

  // Filter group segments
  const filteredPainterSegs = painterSegs.filter(seg => {
    if (!activeGroups[seg.group_name || '']) return false;
    if (sideFilter !== 'All' && seg.side !== sideFilter) return false;
    return true;
  });

  const homePersonSelectList = useMemo(() => {
    const byId = new Set(homePersonOptions.map((i) => i.id));
    const list = homePersonOptions.slice();
    if (homePersonId && !byId.has(homePersonId)) {
      list.push({
        id: homePersonId,
        first_name: '(ID not in current tree list)',
        surname: homePersonId.slice(0, 8) + '…',
      });
    }
    return list.sort(sortHomeIndividuals);
  }, [homePersonOptions, homePersonId]);

  return (
    <section className="genealogy-section">
      <div style={{ display: 'flex', alignItems: 'center', gap: 16, marginBottom: 16, flexWrap: 'wrap' }}>
        <h1 className="heading-1" style={{ margin: 0 }}>Chromosome Painter</h1>
        <label style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
          <span style={{ color: '#aaa', fontSize: 14 }}>Home person (DNA test subject)</span>
          <select
            className="genealogy-btn-secondary"
            style={{ minWidth: 220, padding: '6px 10px', cursor: 'pointer' }}
            value={homePersonId}
            onChange={(e) => setHomePersonId(e.target.value)}
            aria-label="Home person for DNA Painter and chromosome data"
          >
            {homePersonSelectList.map((ind) => (
              <option key={ind.id} value={ind.id}>
                {formatHomeIndividual(ind)}
              </option>
            ))}
          </select>
        </label>
        <div style={{ display: 'flex', gap: 8 }}>
          <button
            className={`genealogy-btn-${view === 'progenitor' ? 'primary' : 'secondary'}`}
            onClick={() => setView('progenitor')}
          >Progenitor View</button>
          <button
            className={`genealogy-btn-${view === 'group' ? 'primary' : 'secondary'}`}
            onClick={() => setView('group')}
          >Group View</button>
          <button
            className={`genealogy-btn-${view === 'mrca' ? 'primary' : 'secondary'}`}
            onClick={() => setView('mrca')}
          >MRCA map</button>
        </div>
        <button className="genealogy-btn-secondary" onClick={() => setImportOpen(true)}>
          Import DNA Painter CSV
        </button>
      </div>

      {/* Import Modal */}
      {importOpen && (
        <div className="modal-overlay">
          <div className="modal-content genealogy-card" style={{ maxWidth: 640, width: '100%' }}>
            <h3 className="heading-3">Import DNA Painter CSV</h3>
            <p style={{ color: '#aaa', marginBottom: 12 }}>
              Paste the contents of your DNA Painter export CSV below.<br />
              Columns required: <code>Chr, Start, End, cM, SNPs, Match, Group, Side, Colour</code>
            </p>
            <textarea
              value={importCsv}
              onChange={e => setImportCsv(e.target.value)}
              placeholder={'Paste CSV here...\n"Chr","Start","End","cM","SNPs","Match","Confidence","Group","Side","Notes","Colour","Ancestor"'}
              style={{ width: '100%', minHeight: 200, fontFamily: 'monospace', fontSize: 12, borderRadius: 6, border: '1px solid #444', background: '#1a1d24', color: '#fff', padding: 10, boxSizing: 'border-box', marginBottom: 12 }}
            />
            <label style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 12 }}>
              <input type="checkbox" checked={clearExisting} onChange={e => setClearExisting(e.target.checked)} />
              Clear existing painter data before import
            </label>
            {importResult && (
              <div style={{ color: importResult.includes('failed') ? 'var(--color-error)' : 'var(--color-success)', marginBottom: 10, fontWeight: 600 }}>{importResult}</div>
            )}
            <div style={{ display: 'flex', gap: 10 }}>
              <button className="genealogy-btn-primary" onClick={handleImport} disabled={importing || !importCsv.trim()}>
                {importing ? 'Importing...' : 'Import'}
              </button>
              <button className="genealogy-btn-secondary" onClick={() => { setImportOpen(false); setImportResult(null); setImportCsv(''); }}>Close</button>
            </div>
          </div>
        </div>
      )}

      {/* ── PROGENITOR VIEW ── */}
      {view === 'progenitor' && (
        <>
          {fetchError && (
            <div className="genealogy-card" style={{ color: 'var(--color-error)', marginBottom: 16, fontWeight: 600 }}>{fetchError}</div>
          )}
          {!progenitorData && !fetchError && painterSegs.length === 0 && !showAncestryRegionsByParent && (
            <div>Loading progenitor data...</div>
          )}

          {/* Show nothing-imported message only when both sources are empty */}
          {!progenitorData && !fetchError && painterSegs.length === 0 && !showAncestryRegionsByParent && (
            <div className="genealogy-card" style={{ textAlign: 'center', padding: '48px 0', color: '#aaa' }}>
              <p>No chromosome data available yet.</p>
              <button className="genealogy-btn-primary" onClick={() => setImportOpen(true)}>Import DNA Painter CSV</button>
            </div>
          )}

          {/* Progenitor legend — only when progenitor lines exist */}
          {progenitorData && Object.keys(allProgenitors).length > 0 && (
            <div className="genealogy-card" style={{ marginBottom: 24, display: 'flex', flexWrap: 'wrap', gap: 12, alignItems: 'center' }}>
              <strong>Progenitor Lines:</strong>
              {Object.entries(allProgenitors).map(([id, name]) => (
                <label key={id} style={{ cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 4 }}>
                  <input
                    type="checkbox"
                    checked={activeProgenitors[id] || false}
                    onChange={() => setActiveProgenitors(p => ({ ...p, [id]: !p[id] }))}
                  />
                  <span style={{ color: progenitorData.progenitor_colors[name] || '#888', fontWeight: 600 }}>{name}</span>
                </label>
              ))}
            </div>
          )}

          {(progenitorData || painterSegs.length > 0 || showAncestryRegionsByParent) && (
            <>
              <div
                className="chromosome-painter-page-ancestry-regions-by-parent-toggle-wrap"
                style={{ marginBottom: 12 }}
              >
                <label
                  className="chromosome-painter-page-ancestry-regions-by-parent-toggle"
                  style={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: 8,
                    cursor: 'pointer',
                    color: '#ccc',
                    userSelect: 'none',
                  }}
                >
                  <input
                    type="checkbox"
                    checked={showAncestryRegionsByParent}
                    onChange={(e) => setShowAncestryRegionsByParent(e.target.checked)}
                  />
                  <span>Ancestry regions by parent (underlay on paternal / maternal bars)</span>
                </label>
              </div>
              <ChromosomePainterCombinedBars
                chromLengths={chromLengths}
                progenitorData={progenitorData}
                activeProgenitors={activeProgenitors}
                painterSegs={painterSegs}
                hovered={hovered}
                setHovered={setHovered}
                groupHovered={groupHovered}
                setGroupHovered={setGroupHovered}
                showAncestryRegionsByParent={showAncestryRegionsByParent}
              />
            </>
          )}
        </>
      )}

      {/* ── MRCA LINEAGE MAP + chromosome overlay (same as Progenitor: breakdown + bars) ── */}
      {view === 'mrca' && (
        <>
          <div className="genealogy-card" style={{ padding: 20, marginBottom: 16 }}>
            <h2 className="heading-2" style={{ marginTop: 0 }}>MRCA lineage map</h2>
            <MrcaLineageMap homeId={homePersonId} />
          </div>

          {fetchError && (
            <div className="genealogy-card" style={{ color: 'var(--color-error)', marginBottom: 16, fontWeight: 600 }}>{fetchError}</div>
          )}

          {progenitorData && Object.keys(allProgenitors).length > 0 && (
            <div className="genealogy-card" style={{ marginBottom: 16, display: 'flex', flexWrap: 'wrap', gap: 12, alignItems: 'center' }}>
              <strong>Progenitor lines (chromosome overlay):</strong>
              {Object.entries(allProgenitors).map(([id, name]) => (
                <label key={id} style={{ cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 4 }}>
                  <input
                    type="checkbox"
                    checked={activeProgenitors[id] || false}
                    onChange={() => setActiveProgenitors((p) => ({ ...p, [id]: !p[id] }))}
                  />
                  <span style={{ color: progenitorData.progenitor_colors[name] || '#888', fontWeight: 600 }}>{name}</span>
                </label>
              ))}
            </div>
          )}

          {(progenitorData || painterSegs.length > 0 || showAncestryRegionsByParent) && (
            <>
              <h2 className="heading-2" style={{ marginBottom: 12 }}>Chromosomes</h2>
              <div
                className="chromosome-painter-mrca-layer-toggles"
                style={{
                  display: 'flex',
                  flexWrap: 'wrap',
                  gap: '10px 22px',
                  marginBottom: 12,
                  alignItems: 'center',
                }}
              >
                <label
                  className="chromosome-painter-mrca-layer-toggle-progenitor"
                  style={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: 8,
                    cursor: 'pointer',
                    color: '#ccc',
                    userSelect: 'none',
                  }}
                >
                  <input
                    type="checkbox"
                    checked={mrcaChromosomeShowProgenitor}
                    onChange={(e) => setMrcaChromosomeShowProgenitor(e.target.checked)}
                  />
                  <span>Progenitor lines on chromosome bars</span>
                </label>
                <label
                  className="chromosome-painter-mrca-layer-toggle-dna-painter-segments"
                  style={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: 8,
                    cursor: 'pointer',
                    color: '#ccc',
                    userSelect: 'none',
                  }}
                >
                  <input
                    type="checkbox"
                    checked={mrcaChromosomeShowDnaPainter}
                    onChange={(e) => setMrcaChromosomeShowDnaPainter(e.target.checked)}
                  />
                  <span>DNA Painter segments (matches / CSV groups)</span>
                </label>
                <label
                  className="chromosome-painter-mrca-path-segment-colors-toggle"
                  style={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: 8,
                    cursor: mrcaChromosomeShowDnaPainter ? 'pointer' : 'not-allowed',
                    color: mrcaChromosomeShowDnaPainter ? '#ccc' : '#666',
                    userSelect: 'none',
                  }}
                >
                  <input
                    type="checkbox"
                    checked={mrcaPathSegmentColorsEnabled}
                    disabled={!mrcaChromosomeShowDnaPainter}
                    onChange={(e) => setMrcaPathSegmentColorsEnabled(e.target.checked)}
                  />
                  <span>MRCA paths (segment colours)</span>
                </label>
                <label
                  className="chromosome-painter-mrca-layer-toggle-ancestry-regions-by-parent"
                  style={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: 8,
                    cursor: 'pointer',
                    color: '#ccc',
                    userSelect: 'none',
                  }}
                >
                  <input
                    type="checkbox"
                    checked={showAncestryRegionsByParent}
                    onChange={(e) => setShowAncestryRegionsByParent(e.target.checked)}
                  />
                  <span>Ancestry regions by parent (underlay)</span>
                </label>
              </div>
              <p style={{ color: '#aaa', marginTop: 0, marginBottom: 16, maxWidth: 720 }}>
                {mrcaChromosomeShowProgenitor && (
                  <span>Progenitor lines use the same colours as elsewhere. </span>
                )}
                {!mrcaChromosomeShowDnaPainter && mrcaChromosomeShowProgenitor && (
                  <span>DNA Painter segments are hidden — only the progenitor overlay is shown.</span>
                )}
                {mrcaChromosomeShowDnaPainter && !mrcaChromosomeShowProgenitor && mrcaPathSegmentColorsEnabled && (
                  <span>
                    Progenitor overlay is hidden. DNA Painter segments use <strong>MRCA path</strong> colours when the match
                    is linked to the map; otherwise CSV group colour.
                  </span>
                )}
                {mrcaChromosomeShowDnaPainter && !mrcaChromosomeShowProgenitor && !mrcaPathSegmentColorsEnabled && (
                  <span>Progenitor overlay is hidden. DNA Painter segments use <strong>CSV group</strong> colours.</span>
                )}
                {mrcaChromosomeShowDnaPainter && mrcaChromosomeShowProgenitor && mrcaPathSegmentColorsEnabled && (
                  <span>
                    {' '}
                    DNA Painter segments use <strong>MRCA path</strong> (e.g. PPPMPP) when linked to the person on that
                    path; unlinked segments keep CSV group colour.
                  </span>
                )}
                {mrcaChromosomeShowDnaPainter && mrcaChromosomeShowProgenitor && !mrcaPathSegmentColorsEnabled && (
                  <span>
                    {' '}
                    DNA Painter segments use <strong>CSV group</strong> colours (turn on MRCA path colours to attribute by
                    lineage).
                  </span>
                )}
              </p>
              <ChromosomePainterCombinedBars
                chromLengths={chromLengths}
                progenitorData={progenitorData}
                activeProgenitors={activeProgenitors}
                painterSegs={painterSegs}
                hovered={hovered}
                setHovered={setHovered}
                groupHovered={groupHovered}
                setGroupHovered={setGroupHovered}
                painterColorMode="mrca_lineage_path"
                mrcaLinkedIndividualIds={mrcaLinkedIndividualIds}
                mrcaPathSegmentColorsEnabled={mrcaPathSegmentColorsEnabled}
                showProgenitorSegments={mrcaChromosomeShowProgenitor}
                showDnaPainterSegments={mrcaChromosomeShowDnaPainter}
                showAncestryRegionsByParent={showAncestryRegionsByParent}
              />
            </>
          )}

          {!progenitorData && !fetchError && painterSegs.length === 0 && !showAncestryRegionsByParent && (
            <div className="genealogy-card" style={{ textAlign: 'center', padding: '32px 16px', color: '#aaa' }}>
              <p style={{ margin: '0 0 12px' }}>No chromosome overlay yet. Import a DNA Painter CSV or ensure progenitor chromosome data is available to see segments on chromosomes here.</p>
              <button className="genealogy-btn-primary" onClick={() => setImportOpen(true)}>Import DNA Painter CSV</button>
            </div>
          )}
        </>
      )}

      {/* ── GROUP VIEW ── */}
      {view === 'group' && (
        <>
          {painterSegs.length === 0 ? (
            <div className="genealogy-card" style={{ textAlign: 'center', padding: '48px 0', color: '#aaa' }}>
              <p>No DNA Painter data imported yet.</p>
              <button className="genealogy-btn-primary" onClick={() => setImportOpen(true)}>Import DNA Painter CSV</button>
            </div>
          ) : (
            <>
              {/* Controls */}
              <div className="genealogy-card" style={{ marginBottom: 16, display: 'flex', flexWrap: 'wrap', gap: 12, alignItems: 'flex-start' }}>
                <div>
                  <strong style={{ display: 'block', marginBottom: 6 }}>Side filter:</strong>
                  {['All', 'Paternal', 'Maternal', 'Unknown or shared'].map(s => (
                    <button key={s}
                      className={`genealogy-btn-${sideFilter === s ? 'primary' : 'secondary'}`}
                      style={{ marginRight: 6, marginBottom: 4, fontSize: 13, padding: '4px 10px' }}
                      onClick={() => setSideFilter(s)}
                    >{s}</button>
                  ))}
                </div>
                <label
                  className="chromosome-painter-group-view-ancestry-regions-by-parent-toggle"
                  style={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: 8,
                    cursor: 'pointer',
                    color: '#ccc',
                    userSelect: 'none',
                    marginTop: 22,
                  }}
                >
                  <input
                    type="checkbox"
                    checked={showAncestryRegionsByParent}
                    onChange={(e) => setShowAncestryRegionsByParent(e.target.checked)}
                  />
                  <span>Ancestry regions by parent (underlay)</span>
                </label>
              </div>
              {/* Group legend */}
              <div className="genealogy-card" style={{ marginBottom: 16, display: 'flex', flexWrap: 'wrap', gap: 10, alignItems: 'center' }}>
                <strong>Groups:</strong>
                {painterGroups.map(g => (
                  <label key={g.name} style={{ display: 'flex', alignItems: 'center', gap: 4, cursor: 'pointer' }}>
                    <input
                      type="checkbox"
                      checked={activeGroups[g.name] || false}
                      onChange={() => setActiveGroups(prev => ({ ...prev, [g.name]: !prev[g.name] }))}
                    />
                    <span style={{ display: 'inline-block', width: 14, height: 14, background: g.colour, borderRadius: 3, border: '1px solid #555' }} />
                    <span style={{ color: g.colour, fontWeight: 600, fontSize: 13 }}>{g.name}</span>
                    <span style={{ color: '#888', fontSize: 12 }}>({g.num_segments} segs, {g.total_cm} cM)</span>
                  </label>
                ))}
              </div>
              {/* Chromosome bars */}
              <div style={{ overflowX: 'auto', background: '#23272f', borderRadius: 12, padding: 24 }}>
                {Object.keys(chromLengths).map(chrom => {
                  const chromSegs = filteredPainterSegs.filter(s => String(s.chromosome) === chrom);
                  const paternalSegs = chromSegs.filter(s => s.side === 'Paternal');
                  const maternalSegs = chromSegs.filter(s => s.side === 'Maternal');
                  const unknownSegs = chromSegs.filter(s => s.side !== 'Paternal' && s.side !== 'Maternal');
                  const rows: { label: string; color: string; segs: PainterSeg[] }[] = [
                    { label: 'Paternal', color: '#b2dfdb', segs: paternalSegs },
                    { label: 'Maternal', color: '#f8bbd0', segs: maternalSegs },
                    ...(unknownSegs.length > 0 ? [{ label: 'Unknown', color: '#bdbdbd', segs: unknownSegs }] : []),
                  ];
                  if (chromSegs.length === 0) return null;
                  return (
                    <div key={chrom} style={{ marginBottom: 24 }}>
                      <div style={{ fontWeight: 600, color: '#fff', marginBottom: 2 }}>Chr {chrom}</div>
                      {rows.map(row => (
                        <div key={row.label} style={{ display: 'flex', alignItems: 'center', marginBottom: 2 }}>
                          <span style={{ width: 70, color: row.color, fontWeight: 500, fontSize: 13 }}>{row.label}</span>
                          <div style={{ position: 'relative', height: 20, background: '#444', borderRadius: 8, flex: 1 }}>
                            {(row.label === 'Paternal' || row.label === 'Maternal') && (
                              <ChromosomePainterAncestryRegionLayer
                                chrom={chrom}
                                chromLength={chromLengths[chrom]}
                                side={row.label === 'Paternal' ? 'Paternal' : 'Maternal'}
                                enabled={showAncestryRegionsByParent}
                                hovered={groupViewAncestryHovered}
                                setHovered={setGroupViewAncestryHovered}
                              />
                            )}
                            {row.segs.map(seg => {
                              const left = (seg.start / chromLengths[chrom]) * 100;
                              const width = ((seg.end - seg.start) / chromLengths[chrom]) * 100;
                              const isHov = groupHovered?.id === seg.id;
                              return (
                                <div key={seg.id}
                                  style={{ position: 'absolute', left: `${left}%`, width: `${Math.max(width, 0.2)}%`, height: 16, top: 0, background: seg.group_colour || '#888', borderRadius: 6, opacity: isHov ? 1 : 0.85, border: isHov ? '2px solid #fff' : 'none', cursor: 'pointer', zIndex: isHov ? 2 : 1 }}
                                  onMouseEnter={() => setGroupHovered(seg)}
                                  onMouseLeave={() => setGroupHovered(null)}
                                  title={`${seg.match_name}${seg.match_source ? ` (${seg.match_source})` : ''} | ${seg.group_name} | ${seg.cm} cM`}
                                />
                              );
                            })}
                            {groupHovered && String(groupHovered.chromosome) === chrom && row.segs.some(s => s.id === groupHovered.id) && (
                              <div className="genealogy-card" style={{ position: 'absolute', left: `${(groupHovered.start / chromLengths[chrom]) * 100}%`, top: 22, color: '#222', borderRadius: 6, padding: '8px 14px', fontSize: 14, boxShadow: '0 2px 8px rgba(0,0,0,0.18)', zIndex: 10, minWidth: 240, background: '#fff' }}>
                                <div><strong>Match:</strong> {groupHovered.match_name}{groupHovered.match_source ? <span style={{ color: '#888' }}> ({groupHovered.match_source})</span> : ''}</div>
                                <div><strong>Group:</strong> <span style={{ color: groupHovered.group_colour }}>{groupHovered.group_name}</span></div>
                                <div><strong>Chr {groupHovered.chromosome}:</strong> {groupHovered.start.toLocaleString()} – {groupHovered.end.toLocaleString()}</div>
                                <div><strong>cM:</strong> {groupHovered.cm} &nbsp; <strong>SNPs:</strong> {groupHovered.snps}</div>
                                {groupHovered.confidence && <div><strong>Confidence:</strong> {groupHovered.confidence}</div>}
                                {groupHovered.notes && <div><strong>Notes:</strong> {groupHovered.notes}</div>}
                              </div>
                            )}
                          </div>
                        </div>
                      ))}
                    </div>
                  );
                })}
              </div>
            </>
          )}
        </>
      )}
    </section>
  );
};

export default ChromosomePainterPage;
